import {useEffect, useRef, useState, forwardRef} from "react";
import { TreeItem } from '@mui/x-tree-view';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import {ArrowUpward, ArrowDownward, Remove, DragIndicator, Edit} from "@mui/icons-material";
import {IconButton, Modal, Box, Typography, Autocomplete} from "@mui/material";
import CustomTextField from "./CustomTextField.tsx";
import {DefaultButton} from "./Buttons.tsx";

interface Item {
    quantity: number;
    text: string;
}

interface AutocompleteNumberTextInputProps {
    items: Item[];
    setItems: React.Dispatch<React.SetStateAction<Item[]>>;
    suggestions: string[];
    headline: string;
    numberfieldLabel: string;
    textfieldLabel: string;
}

const AutocompleteNumberTextInput = forwardRef<HTMLInputElement, AutocompleteNumberTextInputProps>(
    ({ items, setItems, suggestions, headline, numberfieldLabel, textfieldLabel }, ref) => {
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [editText, setEditText] = useState("");
  const [inputQuantity, setInputQuantity] = useState<string>(null);
  const [inputText, setInputText] = useState<string>(null);
  const [quantityError, setInputQuantityError] = useState<boolean>(false);
  const [inputTextError, setInputTextError] = useState<boolean>(false);
  const [inputQuantityTouched, setInputQuantityTouched] = useState(false);
  const [inputTextTouched, setInputTextTouched] = useState(false);
  const inputRef = useRef(null);

    // Sync the forwarded ref with internal ref
    useEffect(() => {
        if (typeof ref === 'function') {          // i.e. callback ref (ref is a function)
            ref(inputRef.current);
        } else if (ref) {                         // i.e. ref created with useRef()
            (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
        }
    }, [ref]);


  const moveItem = (index, direction) => {
    const newItems = [...items];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setItems(newItems);
  };

  const handleDragStart = (index) => setDraggingIndex(index);

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggingIndex === null || draggingIndex === index) return;
    const newItems = [...items];
    const [draggedItem] = newItems.splice(draggingIndex, 1);
    newItems.splice(index, 0, draggedItem);
    setItems(newItems);
    setDraggingIndex(index);
  };

  const handleDrop = () => {
    setDraggingIndex(null);
  };

  const handleEditClick = (index) => {
    setEditIndex(index);
    //setEditQuantity(items[index].quantity);
    setEditQuantity(String(items[index].quantity));
    setEditText(items[index].text);
  };

  // This takes index and field. Doing (e) means that we don't have deliver (index,field,e) when calling this but just (index,field)
  const handleItemChange = (index, field, value) => {
    setItems((prev) => prev.map((item, i) => (i === index ? {... item, [field]: value } : item)));
  };

  useEffect(() => {setInputQuantityError(inputQuantityTouched && (!inputQuantity || Number(inputQuantity) === 0));}, [inputQuantity, inputQuantityTouched]);
  useEffect(() => {setInputTextError(inputTextTouched && !inputText);}, [inputText, inputTextTouched]);

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "80%", gap: "15px", borderStyle: "solid", borderWidth: "1px", borderRadius: "10px", paddingBottom: "15px"}}>
      <h3>{headline}</h3>
      <div style={{ display:'flex', flexDirection: 'row', alignItems: 'start', columnGap: '15px', justifyContent: 'center', width: "100%", minWidth: "100%", marginBottom: "15px" }}>
      <CustomTextField
        label={numberfieldLabel}
        ref={inputRef}
        is_required={true}
        value={inputQuantity}
        onChange={(e) => {
          setInputQuantity(e.target.value);
        }}
        inputMode={"integer"}
        sx={{ width: "135px" }}
        manualError={quantityError}
        errorText={"Die Menge muss größer als 0 sein!"}
        onBlur={()=>{setInputQuantityTouched(true);}}
        onKeyDown={(e) => {
          if (e.key === "Enter" && inputQuantity && Number(inputQuantity) > 0 && inputText) {
            e.preventDefault();
            setItems([...items, { quantity: Number(inputQuantity), text: inputText }]);
            setInputText("");
            setInputQuantity("");
            setInputTextTouched(false);
            setInputQuantityTouched(false);
          }
        }}
      />
      <Autocomplete
        freeSolo
        options={suggestions}
        value={inputText}
        onChange={(_e, newValue) => {setInputText(newValue); setTimeout(() => inputRef.current?.setSelectionRange(0, 0), 0);}}
        onInputChange={(_e, newValue) => setInputText(newValue)}
        renderInput={(params) => (
            <CustomTextField
              {...params}
              value={null}
              label={textfieldLabel}
              is_required={true}
              manualError={inputTextError}
              errorText={"Dieses Feld darf nicht leer sein!"}
              sx={{ width: "500px" }}
              onBlur={()=>{setInputTextTouched(true)}}
              inputRef={inputRef}
              onKeyDown={(e) => {
                if (e.key === "Enter" && inputQuantity && Number(inputQuantity) > 0 && inputText) {
                  e.preventDefault();
                  setItems([...items, { quantity: Number(inputQuantity), text: inputText }]);
                  setInputText("");
                  setInputQuantity("");
                  setInputTextTouched(false);
                  setInputQuantityTouched(false);
                }
              }}
            />
          )}
      />
      <DefaultButton label={"Hinzufügen"}
               onClick={() => {
                 if (inputQuantity && Number(inputQuantity)>0 && inputText) {
                   setItems([...items, {quantity: Number(inputQuantity), text: inputText}]);
                   setInputText("");
                   setInputQuantity("");
                   setInputTextTouched(false);
                   setInputQuantityTouched(false);
                 } else {
                   setInputTextTouched(true);
                   setInputQuantityTouched(true);
                 }}}
               sx = {{ width: "125px", minWidth: "125px", height: "55px"}} variant={"contained"} color={(!inputQuantity || Number(inputQuantity)==0 || !inputText) ? "red" : ""}/>
    </div>
      <SimpleTreeView>
        {items.map((item, index) => (
          <TreeItem
            key={index}
            //itemId={index}
            itemId={String(index)}
            label={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "transparent",
                  padding: "8px",
                  borderRadius: "4px",
                  justifyContent: "center"
                }}
                onDoubleClick={() => handleEditClick(index)}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={handleDrop}
              >
                <IconButton {...{ "aria-label": "drag" }}>
                  <DragIndicator />
                </IconButton>
                <CustomTextField
                  label={numberfieldLabel}
                  value={item.quantity}
                  inputMode={"integer"}
                  sx={{ width: "135px" }}
                  onClick={(e) => e.stopPropagation()}
                  // When live editing should be possible
                  onChange={(e) => {
                    if (e.target.value && Number(e.target.value)>0) {
                      handleItemChange(index, "quantity", Number(e.target.value))
                    }
                    else {handleItemChange(index, "quantity", null)}
                  }}
                  // Set this to true if editing should only be possible with a double-click
                  // inputProps={{readOnly: true}}
                  // onDoubleClick={(e) => {
                  //   e.stopPropagation();
                  //   handleEditClick(index);
                  //}}
                />
                <CustomTextField
                  label={textfieldLabel}
                  value={item.text}
                  sx={{ width: "500px" }}
                  onClick={(e) => e.stopPropagation()}
                  // When live editing should be possible
                  onChange={(e) => handleItemChange(index, "text", e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()} /* Necessary to be able to enter spacebar for some reason */
                  // Set this to true if editing should only be possible with a double-click
                  // inputProps={{readOnly: true}}
                  // onDoubleClick={(e) => {
                  //   e.stopPropagation();
                  //   handleEditClick(index);
                  // }}
                />
                <IconButton onClick={() => moveItem(index, -1)} disabled={index === 0}>
                  <ArrowUpward />
                </IconButton>
                <IconButton onClick={() => moveItem(index, 1)} disabled={index === items.length - 1}>
                  <ArrowDownward />
                </IconButton>
                <IconButton onClick={() => handleEditClick(index)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setItems(items.filter((_, i) => i !== index))} color="error">
                  <Remove />
                </IconButton>
              </div>
            }
            sx={{
              "& .MuiTreeItem-content.Mui-selected": {
                backgroundColor: "transparent",
              },
              "& .MuiTreeItem-content.Mui-selected:hover": {
                backgroundColor: "var(--primary-color)",
              },
              "& .MuiTreeItem-content:hover": {
                backgroundColor: "var(--primary-color)"
              }
            }}
          />
        ))}
      </SimpleTreeView>

      {/* Edit Modal */}
      <Modal open={editIndex !== null} onClose={() => setEditIndex(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "8px",
          }}
        >
          <Typography variant="h6">Eintrag bearbeiten</Typography>
          <p></p>
          <CustomTextField
            label={numberfieldLabel}
            value={editQuantity}
            onChange={(e) => setEditQuantity(e.target.value)}
            inputMode="integer"
            sx={{width: "100%", marginBottom: "16px"}}
            manualError={!editQuantity || Number(editQuantity)==0}
            errorText={"Dieses Feld darf nicht leer oder 0 sein!"}
          />
          <CustomTextField
            label={textfieldLabel}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            sx={{width: "100%", marginBottom: "16px"}}
            manualError={!editText}
            errorText={"Dieses Feld darf nicht leer sein!"}
          />
          <div style={{display: "flex", flexDirection: "row", gap: "15px", justifyContent: "center"}}>
            <DefaultButton label={"Abbrechen"}
                           onClick={() => setEditIndex(null)}
                           sx={{width: "125px", minWidth: "125px", height: "55px"}} color={"red"} variant={"contained"}/>
            <DefaultButton label={"Speichern"}
                           onClick={() => {
                             if (editQuantity && Number(editQuantity)>0 && editText) {
                                setItems(items.map((item, i) => (i === editIndex ? { ...item, quantity: Number(editQuantity), text: editText } : item)));
                                setEditIndex(null);
                             }
                           }}
                           sx={{width: "125px", minWidth: "125px", height: "55px"}}/>
          </div>
        </Box>
      </Modal>
    </div>
);
});

export default AutocompleteNumberTextInput;
